import { globalStorage } from './window';
import { api, VERSION, formatUrl } from './api';
import { IProposalStatuses } from './proposalsApi';
import pickBy from 'lodash/pickBy';
import type { TSubmitInstructionPayload } from './specialist-api/types';

export const PATIENTS_API_URL = {
  CASES: '/cases',
  INVITATIONS: '/cases/invitations',
  ACCEPT_CASE: '/cases/accept',
  REJECT_CASE: '/cases/reject',
  JOURNEY: '/cases/journey',
  LATESTAPPROVEDCASE: '/cases/latest',
  PRIORITY_CASES: '/cases/priority',
  PATIENTS: '/patients',
  PATIENT_CHECKINS: '/patient-checkings',
  TREATMENT_SUBMISSIONS: '/patient-submissions',
  REQUEST_ENVELOPE: '/envelopes',
  DENTIST_RELATION: '/dentist-relation',
};

export interface IClinic {
  id: string;
  name: string;
  address: string;
}

export interface IMobile {
  ext: string;
  number: string;
}

export interface IPatientDetails {
  fullName: string;
  firstName: string;
  lastName: string;
  mobile: IMobile;
  profilePic: string;
  email: string;
  dob: string;
  id: string;
}

export interface IPatinetImage {
  url: string;
  type: string;
  name: string;
}

export const GDP_CASE_STATUS = [
  'draft',
  'submitted',
  'waitingApproval',
  'approved',
  'inTreatment',
  'completed',
  'expired',
  'notSuitable',
  'archived',
] as const;

export type TGDPCaseStatus = typeof GDP_CASE_STATUS[number];

export type TDesignerCaseStatus =
  | 'waitingPlan'
  | 'modificationRequested'
  | 'waitingFiles'
  | 'completed'
  | 'waitingApproval'
  | 'waitingVerification'
  | 'waitingInstructions';

export type TManufacturerCaseStatus = 'newCase' | 'inProgress' | 'shipped';

export type TSpecialistCaseStatus =
  | 'newCase'
  | 'onHold'
  | 'inProgress'
  | 'rejected'
  | 'awaitingApproval'
  | 'completed'
  | 'draft'
  | 'approved'
  | 'expired'
  | 'notSuitable';

export type TCaseStatus = TGDPCaseStatus | TSpecialistCaseStatus | TDesignerCaseStatus | TManufacturerCaseStatus;

export type TRejectedReasons = {
  commentsToPatient: string;
  suggestedNextSteps: string;
  complexity?: string;
  patientGoesAhead?: string;
  implicationsForPatient?: string;
  orthoSuggestions?: string;
};

export type TRejectedReasonsExtended = TRejectedReasons & {
  adminRemarks?: TRejectedReasons;
};

export interface IPatient extends Omit<TSubmitInstructionPayload, 'status' | 'case'> {
  id: string;
  patient: IPatientDetails;
  clinic: IClinic;
  serviceType: TCaseType;
  submissionStatus: 'draft' | 'submitted' | 'archived';
  status: TCaseStatus;
  createdAt: string;
  submittedAt: string;
  assignedAt: string;
  caseId: string;
  txnObjective: string[];
  dentalAndMedicalFit: string;
  patientMotivationAndConcern: string;
  comprehensiveCompromisedOutcomes: string;
  diagnosis: any;
  auxiliaries: any;
  intraOralImages: IPatinetImage[];
  extraOralImages: IPatinetImage[];
  radioGraphImgs: IPatinetImage[];
  imprImages: IPatinetImage[];
  updatedAt: string;
  designerCaseStatus: TDesignerCaseStatus;
  gdpCaseStatus: TGDPCaseStatus;
  designsSubmitted: number;
  latestProposalStatus: IProposalStatuses;
  impressionType: string;
  buttonCutOuts: string[];
  manufacturerShippingLink: string;
  planType: string;
  refinementInfo: IRefinementInfo;
  specialistCaseStatus: TSpecialistCaseStatus;
  manufacturerStatus: TManufacturerCaseStatus;
  createdBy: any;
  priorExtensiveGeneralDentalTreatment: string;
  priorFacialAesthetics: string;
  relevantMedicalSocialAndDrugHistory: string;
  patientOccupation: string;
  otherModifyingFactors: string;
  dietaryFactors: string;
  priorOrthodontics: string;
  toothSurfaceLoss: string;
  oralHealthPathology: string;
  oralHygiene: string;
  gingivalBiotype: string;
  latestSpecialistProposalStatus: IProposalStatuses;
  instructions: any;
  rejectedReason: TRejectedReasonsExtended;
  widerTreatmentPlan: any;
  widerTreatmentPlanDetails: string;
  patientSuitability: string;
  bruxer: string;
  statedTimings: string;
  generalDentalTreatmentDetail: string;
  priorOrthoDetails: string;
  priorFacialAestheticsDetails: string;
  BPE: string;
  dentistPerspectiveOne: string;
  dentistPerspectiveTwo: string;
  dentistPerspectiveThree: string;
  postageDate: string;
  specialist: {
    id: string;
    personalDetails: any;
    preferences: any;
  };
  activeStep: number;
  crossBite: any;
  patientAcceptableInterventions: any;
  order: any;
  isTreatmentCheckinActive: boolean;
  delay?: {
    status: 'active' | 'disabled';
    dueAt: string;
  };
  proposals?: any;
  materialType?: string;
  finishOptionText?: string;
  anonymisedInfo?: boolean;
  animationUrl?: string;
  outboundInvoiceDate?: string;

  // new fields of Duo case
  dentalHealthStatus?: string;
  statedTimingsNotes?: string;
  TMJ?: string;
  paraFuctionalActivity?: string;
  skeletalPatternAntero?: string;
  skeletalPatternVertical?: string;
  skeletalPatternTransverse?: string;
  crowdingInUpperArch?: string;
  crowdingInLowerArch?: string;
  incisorRelationship?: string[];
  overjetIncisorRelationship?: string;
  overjetInMM?: { lower: number; upper: number };
  overjetOverbiteOrAnterior?: string;
  lowerDentalCenterLineOfFace?: string;
  molarRelations?: string;
  notesToSelf?: string;
  stlUploadType?: string;
  impressionScannerType?: string;
  radiographQuery?: string;
  radiographQueryDetails?: string;
  archiveReason?: string;
  expiredInDays?: number;
  onHoldReason?: string;
  onHoldDetails?: {
    autoUnHoldAt: string | null;
    onHoldAt: string;
    reason: string;
    resolutionTime?: string;
  };
  submissionPercentage?: number;
  designerProposalStatus: IProposalStatuses;
}

export interface IRefinementInfo {
  arches: string;
  stepReached: string;
  stepReachedComments: string;
  treatmentBackground: string;
  treatmentPlanAdditionalComments: string;
}

export interface IPatientsResponse {
  cases: IPatient[];
  total: number;
}
export interface IPatientsRequest {
  page: number;
  perPage: number;
  status: string;
  submissionStatus: string;
  q?: string;
  type?: string;
}

export interface IPatientJourney {
  draft: IPatient[];
  submitted: IPatient[];
  waitingApproval: IPatient[];
  approved: IPatient[];
  inTreatment: IPatient[];
  completed: IPatient[];
}

export interface IPriorityCaseImage {
  url: string;
  type: string;
  name: string;
}

export interface IPriorityCase {
  id: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  caseId: string;
  specialist?: {
    id: string;
    personalDetails: {
      firsName: string;
      lastName: string;
    };
  };
  designsSubmitted: number;
  latestProposalStatus: string;
  deletedAt: string;
  manufacturerStatus: string;
  specialistCaseStatus: string;
  outboundInvoiceDate: string;
  serviceType: string;
  planType: string;
  extraOralImages: IPriorityCaseImage[];
  intraOralImages: IPriorityCaseImage[];
  radioGraphImgs: IPriorityCaseImage[];
  impressionType: string;
  imprImages: IPriorityCaseImage[];
  submissionStatus: string;
  planObjective: string[];
  generalComment: string;
  dentalMidlineUpper: string;
  dentalMidlineLower: string;
  incisorRelationshipOverjet: string;
  incisorRelationshipOverbite: string;
  archCoordination: string;
  anteroposteriorRelationship: string;
  anteroposteriorDetail: string;
  upperArchCrowdingResolutions: string[];
  lowerArchCrowdingResolutions: string[];
  extractions: string[];
  extractionsDetail: string;
  exclusionsSite: string[];
  spaceAims: string;
  attachmentAllow: string;
  attachmentDetail: string;
  attachmentTeethToAvoid: string[];
  iprDetail: string;
  iprTeethToAvoid: string[];
  auxiliaries: string[];
  buttonCutOuts: string[];
  buttonCutOutStage: string;
  passiveAligners: string;
  overcorrectionsDefine: string;
  diagnosis: {
    upperDentalCenterLineOfFace: string;
  };
  lowerDentalCenterLineOfFace: string;
  lowerDentalCenterLineOfChin: string;
  overjetInMM: number;
  overjetIncisorRelationship: string;
  overjetOverbiteOrAnterior: string;
  molarRelations: string;
  molarRelationsLeft: string;
  dentalAndMedicalFit: string;
  txnObjective: string[];
  activeStep: number;
  anonymisedInfo?: boolean;
  patient: {
    profilePic: string;
    firstName: string;
    lastName: string;
    patientId: string;
  };
  designerCaseStatus: string;
  gdpCaseStatus: string;
  requireAttention: boolean;
  assignedAt: string;
  isPriority: boolean;
  onPriorityAt: string;
  createdBy: {
    personalDetails: {
      firstName: string;
      lastName: string;
    };
    id: string;
  };
  designer: {
    id: string;
    personalDetails: {
      firstName: string;
      lastName: string;
    };
  };
  manufacturer?: {
    id: string;
    personalDetails: {
      firsName: string;
      lastName: string;
    };
  };
  delay: {
    id: string;
    createdAt: string;
    updatedAt: string;
    status: string;
    notified: boolean;
    userType: string;
    dueAt: string;
  };
}

export interface IPriorityCasesResponse {
  cases: IPriorityCase[];
  total: number;
}

export type TVerifyPatientTokenResponse = {
  id: string;
  patient: {
    id: string;
    fullName: string;
  };
};

export interface IArchivePatientRequest {
  reason: string;
  id: string;
}

export const patientsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPatients: build.query<IPatientsResponse, IPatientsRequest>({
      query: ({ page, perPage, submissionStatus, status, type, q }) => {
        globalStorage.patientsScreen.urlQueries = { page, perPage, submissionStatus, status, type, q };
        let paramsString = '';
        if (page) paramsString += `page=${page}`;
        if (perPage) paramsString += `&perPage=${perPage}`;
        if (submissionStatus) paramsString += `&submissionStatus=${submissionStatus}`;
        if (status) paramsString += `&status=${status}`;
        if (q) paramsString += `&q=${q}`;

        return formatUrl(`${PATIENTS_API_URL.CASES}?${paramsString}`);
      },
      // Provides a list of `Patients` by `id`.
      // If any mutation is executed that `invalidate`s any of these tags, this query will re-run to be always up-to-date.
      // The `LIST` id is a "virtual id" we just made up to be able to invalidate this query specifically if a new `Posts` element was added.
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [...result.cases.map(({ id }) => ({ type: 'Patients', id } as const)), { type: 'Patients', id: 'LIST' }]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Patients', id: 'LIST' }` is invalidated
            [{ type: 'Patients', id: 'LIST' }],
      // keepUnusedDataFor: 5,
    }),
    searchPatients: build.query<IPatientsResponse, string>({
      query: (q) => formatUrl(`${PATIENTS_API_URL.CASES}?planType=initial&page=1&perPage=20&q=${q}`),
    }),
    addPatient: build.mutation<IPatient, Partial<IPatient>>({
      query(body) {
        return {
          url: formatUrl(PATIENTS_API_URL.CASES),
          method: 'POST',
          body,
        };
      },
      // Invalidates all Post-type queries providing the `LIST` id - after all, depending of the sort order,
      // that newly created post could show up in any lists.
      invalidatesTags: [{ type: 'Patients', id: 'LIST' }],
    }),
    getPatient: build.query<IPatient, string>({
      query: (id) => formatUrl(`${PATIENTS_API_URL.CASES}/${id}`),
      providesTags: (result, error, id) => [{ type: 'Patients', id }],
    }),
    updatePatient: build.mutation<IPatient, Partial<IPatient>>({
      query(data) {
        const { id, createdAt, updatedAt, caseId, designerCaseStatus, gdpCaseStatus, ...body } = data;
        return {
          url: formatUrl(`${PATIENTS_API_URL.CASES}/${id}`),
          method: 'PUT',
          body,
        };
      },
      // Invalidates all queries that subscribe to this Patients `id` only.
      // In this case, `getPatients` will be re-run. `getPatients` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id: _id }) => {
        if (error) return [];
        return [{ type: 'Patients', id: result?.id }];
      },
    }),
    archivePatient: build.mutation<{ message: string }, IArchivePatientRequest>({
      query({ reason, id }) {
        const body = { reason };
        return {
          url: formatUrl(`${PATIENTS_API_URL.CASES}/${id}/archive`),
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: [{ type: 'Patients', id: 'LIST' }],
    }),
    unArchivePatient: build.mutation<{ message: string }, IArchivePatientRequest>({
      query({ reason, id }) {
        const body = { reason };
        return {
          url: formatUrl(`${PATIENTS_API_URL.CASES}/${id}/unarchive`),
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: [{ type: 'Patients', id: 'LIST' }],
    }),
    updatePatientProfile: build.mutation<IPatient, { id: string; data: any }>({
      query({ id, data }) {
        return {
          url: formatUrl(`${PATIENTS_API_URL.PATIENTS}/${id}`),
          method: 'PUT',
          body: data,
        };
      },
    }),
    getPatientsJourney: build.query<IPatientJourney, any>({
      query: () => formatUrl(`${PATIENTS_API_URL.JOURNEY}`),
    }),
    getLatestApprovedCase: build.query<any, any>({
      query: () => formatUrl(`${PATIENTS_API_URL.LATESTAPPROVEDCASE}`),
    }),
    getCasesByPatient: build.query<any, any>({
      query: (id) => formatUrl(`${PATIENTS_API_URL.PATIENTS}/${id}${PATIENTS_API_URL.CASES}`),
    }),
    submitCaseNotSuitable: build.mutation<IPatient, Partial<IPatient>>({
      query(data) {
        const { caseId, ...body } = data;
        return {
          url: formatUrl(`${PATIENTS_API_URL.CASES}/${caseId}/unsuitable`),
          method: 'PUT',
          body,
        };
      },
      // Invalidates all queries that subscribe to this Patients `id` only.
      // In this case, `getPatients` will be re-run. `getPatients` *might*  rerun, if this id was under its results.
      invalidatesTags: (_result, _error, { id: _id }) => [{ type: 'Patients', id: 'LIST' }],
    }),
    updateOnboardingViewed: build.mutation<any, any>({
      query: (credentials) => ({
        url: `/user/${VERSION}/onboardings/viewed`,
        method: 'PUT',
        body: credentials,
      }),
    }),
    getPatientTreatmentSubmissions: build.query<any, any>({
      query: (caseId) => formatUrl(`${PATIENTS_API_URL.TREATMENT_SUBMISSIONS}?caseId=${caseId}`),
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.checkings.map(({ id }: { id: string }) => ({ type: 'TreatmentSubmissions', id } as const)),
              { type: 'TreatmentSubmissions', id: 'LIST' },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'TreatmentSubmissions', id: 'LIST' }` is invalidated
            [{ type: 'TreatmentSubmissions', id: 'LIST' }],
    }),
    verifyPatientToken: build.query<TVerifyPatientTokenResponse, string>({
      query: (token) => `/user/${VERSION}/patient-checkings/password/${token}`,
    }),
    getTreatmentCheckin: build.mutation<any, string>({
      query: (caseId) => ({
        url: formatUrl(`/patient-checkings?caseId=${caseId}`),
        method: 'GET',
      }),
    }),
    createTreatmentCheckin: build.mutation<any, any>({
      query: (data) => ({
        url: formatUrl(`/patient-checkings`),
        method: 'POST',
        body: data,
      }),
    }),
    updateTreatmentCheckin: build.mutation<any, any>({
      query: ({ data, id }) => ({
        url: formatUrl(`/patient-checkings/${id}`),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error) => [{ type: 'Patients', id: 'LIST' }],
    }),
    submitTreatmentCheckin: build.mutation<any, any>({
      query: (data) => ({
        url: `/user/${VERSION}/patient-submissions`,
        method: 'POST',
        body: data,
      }),
    }),
    markPatientStatusViewed: build.mutation<any, any>({
      query: (id) => ({
        url: formatUrl(`/submission-histories/${id}`),
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (_result, _error) => [{ type: 'TreatmentSubmissions', id: 'LIST' }],
    }),
    requestEnvelope: build.mutation<void, void>({
      query: () => ({
        url: formatUrl(PATIENTS_API_URL.REQUEST_ENVELOPE),
        method: 'GET',
      }),
    }),
    priorityCases: build.query<IPriorityCasesResponse, { page: number; perPage: number }>({
      query: (params) => ({
        url: `${formatUrl(PATIENTS_API_URL.PRIORITY_CASES)}?${new URLSearchParams(pickBy(params as any))}`,
      }),
    }),
    getCasesDentistRelation: build.query<any, any>({
      query: (id) => formatUrl(`${PATIENTS_API_URL.CASES}/${id}${PATIENTS_API_URL.DENTIST_RELATION}`),
    }),
  }),
});

export const {
  useGetPatientsQuery,
  useSearchPatientsQuery,
  useAddPatientMutation,
  useGetPatientQuery,
  useUpdatePatientMutation,
  useGetPatientsJourneyQuery,
  useGetLatestApprovedCaseQuery,
  useGetCasesByPatientQuery,
  useSubmitCaseNotSuitableMutation,
  useGetPatientTreatmentSubmissionsQuery,
  useVerifyPatientTokenQuery,
  useGetTreatmentCheckinMutation,
  useCreateTreatmentCheckinMutation,
  useUpdateTreatmentCheckinMutation,
  useSubmitTreatmentCheckinMutation,
  useMarkPatientStatusViewedMutation,
  useRequestEnvelopeMutation,
  usePriorityCasesQuery,
  useUpdatePatientProfileMutation,
  useGetCasesDentistRelationQuery,
  useArchivePatientMutation,
  useUnArchivePatientMutation,
} = patientsApi;
